/**
 * The window's first `popstate` listener, ahead of Next's router.
 *
 * Listeners on one target run in registration order, and Next's app router
 * adds its own when it hydrates, so a listener a component adds afterwards
 * runs too late to stop a traversal from routing. `instrumentation-client.ts`
 * installs this one before the app loads. It forwards each event to the one
 * handler a page registered, which may call `stopImmediatePropagation()` to
 * keep Next from routing. With no handler registered it does nothing.
 */

type PopStateHandler = (event: PopStateEvent) => void;

/** Marks the unsaved-changes guard's history entry in its state. */
export const GUARD_STATE_KEY = '__unsavedChangesGuard';

let handler: PopStateHandler | null = null;
let installed = false;

export function installPopstateDispatcher(): void {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;
  // Capture as well: at the target, capture listeners run before bubble ones.
  window.addEventListener('popstate', (event) => handler?.(event), true);
  keepGuardMarkOnReplace();
}

/**
 * Next rewrites the current entry's state with only its own keys on every
 * commit, a `router.refresh()` included, which would erase the guard's mark
 * and leave an unmarked duplicate entry. Wrapped before Next wraps it, a
 * replace that keeps the URL keeps the mark; one to another URL drops it.
 */
function keepGuardMarkOnReplace() {
  const replaceState = window.history.replaceState.bind(window.history);
  window.history.replaceState = (data, unused, url) => {
    const mark = window.history.state?.[GUARD_STATE_KEY];
    const sameUrl =
      url == null ||
      new URL(String(url), window.location.href).href === window.location.href;
    const kept =
      mark && sameUrl && (data == null || typeof data === 'object')
        ? { ...data, [GUARD_STATE_KEY]: mark }
        : data;
    replaceState(kept, unused, url);
  };
}

/** Whether a handler can reach events before Next does. */
export function popstateDispatcherInstalled(): boolean {
  return installed;
}

/** Registers the page's handler; the returned call removes it if still current. */
export function setPopstateHandler(next: PopStateHandler): () => void {
  handler = next;
  return () => {
    if (handler === next) {
      handler = null;
    }
  };
}
